import { Action } from "redux";
import { Language, Country } from "../";
import * as Distance from "./Distance";
import * as Footer from "./Footer";
import * as Profile from "./Profile";
import * as AddOn from "./AddOn";
import * as Campaign from "./Campaign";
import * as DealerPrice from "./DealerPrice";
import * as Usp from "./USP";
import * as YoungDriver from "./YoungDriver";
import * as DamageFreeYear from "./DamageFreeYear";
import * as Insurance from "./Insurance";
import * as PaymentType from "./PaymentType";
import * as ComponentTexts from "./ComponentTexts";
import * as SingleRecomendation from "./SingleRecommendation";
import * as SigningService from "./SigningService";
import { errorReducer, ErrorState } from "./error";

/* defaults */
import InitTask from "./fetch/init";
import PriceTask from "./fetch/calculate";
import PurchaseTask from "./fetch/purchase";

/* car api */
import CarApiSeInitTask from "./fetch/api_se/init";
import CarApiSePriceTask from "./fetch/api_se/calculate";
import CarApiSetPurchaseTask from "./fetch/api_se/purchase";

import EncryptTask from "./fetch/encrypt";

import { format, isAfter, isToday } from "date-fns";
import { BrandsEnum } from "../enums";
import { RedirectToUrl } from "../helperFunctions";
import { COMPLETED, CompleteAction, RECEIVE_QUOTE, createCompleteAction } from "./actions";
import { SET_SERVER_ERROR, SET_ERROR } from "./actions";
import {
  RECEIVE_PRICE,
  REQUEST_PRICE,
  PROCESSING_START,
  PROCESSING_END,
  ProcessingAction,
  createStartProcessingAction,
  createStopProcessingAction,
} from "./actions";

import { RECEIVE_INITIAL, InitialRequestAction, createInitialRequestAction } from "./actions";

export * from "./configureState";

export interface ApplicationState {
  campaign: Campaign.CampaignState | undefined;
  dealerPrice: DealerPrice.DealerPriceState | undefined;
  usp: Usp.USPState | undefined;
  distance: Distance.DistanceState | undefined;
  footer: Footer.FooterState | undefined;
  profile: Profile.ProfileState | undefined;
  payment: PaymentType.PaymentTypeState | undefined;
  addon: AddOn.AddOnState | undefined;
  youngDriver: YoungDriver.YoungDriverState | undefined;
  damageFreeYear: DamageFreeYear.DamageFreeYearState | undefined;
  insurance: Insurance.InsuranceState | undefined;
  texts: ComponentTexts.Texts | undefined;
  singleRecomendation: SingleRecomendation.SingleRecommendationState | undefined;
  signingService: SigningService.SigningService | undefined;
  ui: UI;
  inputs: {
    crn: string;
    orn?: string;
    ssn: string;
    firstName?: string;
    lastName?: string;
    apiHost: string;
    cmsHost: string;
    webshopPageGuid: string;
    redirectLink: string;
    errorUrl: string;
    orderId: string;
    quoteId?: string;
    isRemote: boolean;
    isKamux: boolean;
    isAfpris: boolean;
    partner: BrandsEnum;
    brand: string;
    webshopMode: number;
    campaignId: string;
    disableBuying: boolean;
    calculationMethod: number;
    confirmationPageUrl?: string;
    car?: {
      code?: string;
      model?: string;
      variant?: string;
      yearFrom?: string;
      yearTo?: string;
    };
    quoteNumber: string;
    useCarApiV2?: boolean;
    isCommercial?: boolean;
    language: Language;
    country: Country;
    decoupledRendering: {
      mainLogo: string;
      mainLogoAlt: string;
      secondaryLogo: string;
      secondaryLogoAlt: string;
      isDecoupledRendering: boolean;
    };
  };
  error: ErrorState;
}

const done = "/done";
const checkout = "/checkout";

let base = "/"; //window?.location?.pathname ?? "/";

// better route fix;
const i = base.indexOf(done);

if (i > -1) {
  base = base.substring(0, i + 1);
}

export const fixPath = (path: string) => `${base.replace(/\/$/, "")}${path}`;

export const CHECKOUT_ROUTE = fixPath(checkout);
export const DONE_ROUTE = fixPath(done);

export const BASE_ROUTE = base;

export interface UI {
  initialized: boolean;
  completed: boolean;
  processing: boolean;
}

export function buildReducers(api = "v1") {
  return {
    campaign: Campaign.reducer,
    dealerPrice: DealerPrice.reducer,
    usp: Usp.reducer,
    distance: Distance.reducer,
    payment: PaymentType.reducer,
    footer: Footer.buildReducer(api),
    profile: Profile.reducer,
    addon: AddOn.reducer,
    singleRecomendation: SingleRecomendation.reducer,
    youngDriver: YoungDriver.reducer,
    damageFreeYear: DamageFreeYear.reducer,
    insurance: Insurance.reducer,
    texts: ComponentTexts.reducer,
    signingService: SigningService.reducer,
    error: errorReducer,
    decoupledRendering: (state = null) => state,
    // responsible for UI interaction;

    inputs: (state = {}) => state,
    ui: (
      state: UI = {
        initialized: false,
        completed: false,
        processing: false,
      },
      action: Action,
    ) => {
      switch (action.type) {
        case PROCESSING_START:
          return { ...state, processing: true };
        case PROCESSING_END:
          return { ...state, processing: false };
        case COMPLETED:
          return { ...state, completed: true };
        case SET_ERROR:
          // Client-side validation error handling (UI only)
          // This does not affect global error dialog
          return {
            ...state,
            error: (action as SetErrorAction).payload,
          };
        default:
          return state;
      }
    },
  };
}

export interface AppThunkAction<TAction> {
  (dispatch: (action: TAction) => void, getState: () => ApplicationState): void;
}

export const REQUEST_ENCRYPTED_CUSTOMER = "REQUEST_ENCRYPTED_CUSTOMER";
export const RECEIVE_ENCRYPTED_CUSTOMER = "RECEIVE_ENCRYPTED_CUSTOMER";
export const CREATED = "CREATED";

export const MODEL_IS_VALID = "MODEL_IS_VALID";

export type ModelIsValidAction = { type: typeof MODEL_IS_VALID };

export interface SetServerErrorAction {
  type: typeof SET_SERVER_ERROR;
  payload: Error;
}

export interface SetErrorAction {
  type: typeof SET_ERROR;
  payload: any;
}

export interface ReceiveAction {
  type: typeof RECEIVE_INITIAL | typeof RECEIVE_QUOTE | typeof RECEIVE_PRICE | typeof RECEIVE_ENCRYPTED_CUSTOMER | typeof CREATED;
  payload: any;
}

export interface PriceRequestAction {
  type: typeof REQUEST_PRICE;
}

export interface EncryptedCustomerRequestAction {
  type: typeof REQUEST_ENCRYPTED_CUSTOMER;
}

export type InitialActions = InitialRequestAction | ReceiveAction;
export type PriceActions = PriceRequestAction | ReceiveAction;
export type EncryptedCustomerActions = EncryptedCustomerRequestAction | ReceiveAction;

export const remoteActions = {
  requestInitialData:
    (): AppThunkAction<InitialActions | ProcessingAction | ReceiveAction | SetServerErrorAction> => async (dispatch, getState) => {
      const appState = getState();
      if (appState) {
        if (appState.ui.initialized) {
          return;
        }

        dispatch(createStartProcessingAction());
        dispatch(createInitialRequestAction());

        // get data from model;
        const state = getState();
        let hadError = false;

        try {
          if ((state.inputs.crn === null && state.inputs.calculationMethod !== 2) || (state.inputs.ssn ?? state.inputs.orn) === null) {
            RedirectToUrl(state.inputs.errorUrl);
            return;
          }

          const appState = state.inputs.useCarApiV2 ? await CarApiSeInitTask(state) : await InitTask(state);

          dispatch({
            type: RECEIVE_INITIAL,
            payload: appState,
          });
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: ex as Error,
          });
          hadError = true;
        } finally {
          dispatch(createStopProcessingAction());
        }

        if (hadError) {
          //  RedirectToUrl(state.inputs.errorUrl);
        }
      }
    },
  requestPriceData: (): AppThunkAction<PriceActions | ProcessingAction | SetServerErrorAction> => async (dispatch, getState) => {
    const state = getState();
    const isValid = state?.ui?.initialized && !!state.distance?.selected;

    if (isValid) {
      dispatch(createStartProcessingAction());
      try {
        const appState = state.inputs.useCarApiV2 ? await CarApiSePriceTask(getState(), dispatch) : await PriceTask(getState(), dispatch);
        dispatch({
          type: RECEIVE_PRICE,
          payload: appState,
        });
      } catch (ex) {
        dispatch({
          type: SET_SERVER_ERROR,
          payload: ex as Error,
        });
      } finally {
        dispatch(createStopProcessingAction());
      }
    }
  },
  requestEncryptedCustomerData:
    (): AppThunkAction<EncryptedCustomerActions | ProcessingAction | SetServerErrorAction> => async (dispatch, getState) => {
      const state = getState();

      const isValid = state?.ui?.initialized;

      if (isValid) {
        dispatch(createStartProcessingAction());
        try {
          const appState = await EncryptTask(getState());
          dispatch({
            type: RECEIVE_ENCRYPTED_CUSTOMER,
            payload: appState,
          });
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: ex as Error,
          });
        } finally {
          dispatch(createStopProcessingAction());
        }
      }
    },
  validateDamageFree: (): AppThunkAction<DamageFreeYear.DamageFreeYearAction> => (dispatch, getState) => {
    const state = getState();
    if (state.damageFreeYear?.selected === null) {
      dispatch({
        type: DamageFreeYear.DAMAGE_FREE_YEAR_INVALID,
        payload: { value: state.damageFreeYear?.selected },
      });
    } else {
      dispatch({
        type: DamageFreeYear.DAMAGE_FREE_YEAR_VALID,
        payload: { value: state.damageFreeYear?.selected },
      });
    }
  },
  validateYoungDriver: (): AppThunkAction<YoungDriver.YoungDriverAction> => (dispatch, getState) => {
    const state = getState();
    if (state.youngDriver?.selected === null) {
      dispatch({
        type: YoungDriver.YOUNG_DRIVER_INVALID,
        payload: { value: state.youngDriver?.selected },
      });
    } else {
      dispatch({
        type: YoungDriver.YOUNG_DRIVER_VALID,
        payload: { value: state.youngDriver?.selected },
      });
    }
  },
  validateSelfRisk: (): AppThunkAction<Insurance.ValidAction> => (dispatch, getState) => {
    const state = getState();
    if (!state?.insurance?.coverage?.SelectedDeductible) {
      dispatch({
        type: Insurance.DEDUCTIBLE_INVALID,
        payload: { value: true },
      });
    } else {
      dispatch({
        type: Insurance.DEDUCTIBLE_VALID,
        payload: { value: false },
      });
    }
  },
  singleRecommendedCoverage: (): AppThunkAction<Insurance.ValidAction> => dispatch => {
    dispatch({
      type: Insurance.SINGLE_RECOMMENDED_COVERAGE_CHANGED,
      payload: { value: false },
    });
  },
  purchase:
    (): AppThunkAction<
      | ReceiveAction
      | CompleteAction
      | ProcessingAction
      | SetServerErrorAction
      | ModelIsValidAction
      | PaymentType.PaymentAction
      | DamageFreeYear.DamageFreeYearAction
      | YoungDriver.YoungDriverAction
      | Insurance.ValidAction
      | SetErrorAction
    > =>
    async (dispatch, getState) => {
      /* waiting for the parallel process will end  */
      if (getState().ui.processing) {
        await new Promise<void>(resolve => {
          const loop = () => {
            setTimeout(() => {
              if (getState().ui.processing) {
                loop();
              } else {
                resolve();
              }
            }, 200);
          };
          if (getState().ui.processing) {
            loop();
          }
        });
      }

      /* protection */
      if (getState().ui.completed) {
        return;
      }

      dispatch({
        type: MODEL_IS_VALID,
      });

      const state = getState();
      const isPaymentTypeBankCodeValid = PaymentType.validateSelectedPaymentType(state.payment);
      if (isPaymentTypeBankCodeValid) {
        dispatch({
          type: PaymentType.BANK_CODE_VALID,
          data: {
            paymentCode: state.payment?.selectedPaymentTypeCode,
            bankCode: state.payment?.selectedBankCode,
          },
        });
      } else {
        dispatch({
          type: PaymentType.BANK_CODE_INVALID,
          data: {
            paymentCode: state.payment?.selectedPaymentTypeCode,
            bankCode: state.payment?.selectedBankCode,
          },
        });
      }
      // SET_ERROR handling removed for now

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const isValid =
        state?.ui?.initialized &&
        state.profile?.isValid &&
        state.distance?.selected &&
        !state.damageFreeYear?.showError &&
        !state.youngDriver?.showError &&
        isPaymentTypeBankCodeValid &&
        state?.payment?.inputs?.isValid &&
        !state?.insurance?.coverage?.showDeductibleError &&
        (state.profile?.isCarNew || //new cars don't have start date field
          state?.insurance?.startDate.isDisabled || // No validation needed if start date is disabled
          (state?.insurance?.startDate.value &&
            (isAfter(state?.insurance?.startDate?.value, today) || isToday(state?.insurance?.startDate?.value))));

      if (isValid) {
        dispatch(createStartProcessingAction());
        try {
          const d: string = state.distance?.selected || "0";

          // +d cast to int;
          const payload = {
            crn: state.inputs.crn,
            ssn: state.inputs.ssn,
            distance: +d,
            paymentTypeCode: state.payment?.selectedPaymentTypeCode,
            paymentBankCode: state.payment?.selectedBankCode,
            apiHost: state.inputs.apiHost,
            cmsHost: state.inputs.cmsHost,
            webshopPageGuid: state.inputs.webshopPageGuid,
            brand: state.inputs?.partner ?? BrandsEnum.Polestar,
            email: state.profile?.email.value?.toString() ?? "",
            phoneNumber: state.profile?.phone.value?.toString() ?? "",
            coverageInternalCode: state.insurance?.coverage?.selectedCode ?? "",
            startDate: state.insurance?.startDate.value ? format(state.insurance.startDate.value, "yyyy-MM-dd") : "",
            orderId: state.inputs.orderId,
            addonInternalCodes: AddOn.GetSelectedAddonsForCoverage(state.addon, state.insurance),
            carHadAccidents:
              state.damageFreeYear?.selected == null || !state.damageFreeYear.questionVisible ? null : state.damageFreeYear.selected,
            campaignId: state.profile?.campaignId ?? state.inputs.campaignId ?? "",
            deductibleCode: state.insurance?.coverage?.SelectedDeductible ?? "",
            glassDeductibleCode: state.insurance?.coverage?.SelectedGlassDeductible ?? "",
            youngDriver: state.youngDriver?.selected ?? null,
            bankAccountOwnerName: state.payment?.inputs?.bankAccountOwnerName?.value ?? "",
            bankAccountOwnerSSN: state.payment?.inputs?.bankAccountOwnerSSN?.value ?? "",
            bankAccountNumber: state.payment?.inputs?.bankAccountNumber?.value ?? "",
            bankAccountClearingNumber: state.payment?.inputs?.bankAccountClearingNumber?.value ?? "",
            bankNotAccountOwner: state.payment?.inputs?.bankAccountOwner?.notOwner ?? false,
            receiveInsuranceType: state.insurance?.insuranceReceive?.selectedValue ?? "",
            volvoCardOwner: !state.profile?.volvoCardNotOwner,
            webshopMode: state.inputs.webshopMode,
            quoteNumber: state.inputs.quoteNumber,
            CalculationMethod: String(state.inputs.calculationMethod),
            CarCode: state.inputs.car?.code ?? "",
            disableBuying: state.inputs.disableBuying,
          };

          const response = state.inputs.useCarApiV2 ? await CarApiSetPurchaseTask(state) : await PurchaseTask(payload, state);

          if (response && response.signingService?.signingService && response.signingService.signingServiceUrl) {
            window.open(response.signingService.signingServiceUrl, "_blank");
          }

          dispatch({
            type: CREATED,
            payload: response,
          });

          dispatch(createCompleteAction());
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: ex as Error,
          });
        } finally {
          dispatch(createStopProcessingAction());
        }
      } else {
        dispatch({
          type: SET_ERROR,
          payload: "Purchase validation error",
        });
      }
    },
};
